import classNames from 'classnames'
import { Component } from 'preact'
import globals from '../../utils/globals'
import userAvatar from '../../utils/userAvatar'
// import LazyLoader from '../lazy-loading/LazyLoader'
import ProfilePopup from '../profile-popup/index'
import LazyImage from '@insided/shared/uikit/LazyImage'
import { getInsidedData } from '@insided/destination/utils/insidedDataStorage'

export interface User<WithRank extends boolean | undefined = undefined> {
  id: number | string
  name: string
  avatar?: string
  url?: string
  rank?: WithRank extends true ? UserRank : undefined
}

type UserRank = {
  avatarIconUrl?: string
  name: string
} | null

export interface UserAvatarProps<WithRank extends boolean | undefined> {
  user: User<boolean | undefined>
  extraClass?: string
  disablePopup?: boolean
  showRank?: WithRank
  disableLink?: boolean
  openInNewTab?: boolean
  style?: JSX.HTMLAttributes['style']
  optimizeAvatarResolution?: boolean
}

interface State {
  ref: boolean
  disablePopup: boolean
  disableLink: boolean
}

// avatarImageUrl is in the format "{CDN_URL}/{COMMUNITY_NAME}/icon/{IMAGE_SIZE}/{IMAGE_ID}.png"
function transformAvatarImageUrlSize(avatarImageUrl: string, newSize: string): string {
  const avatarImageUrlChunks = avatarImageUrl.split('/')
  avatarImageUrlChunks[avatarImageUrlChunks.length - 2] = newSize
  return avatarImageUrlChunks.join('/')
}

// TODO: Replace this funcitonality once the backend call has been chaned to return the URLs for different sizes by default
function getOptimizedAvatarImageElement(avatarImageUrl: string, userName: string) {
  const avatarImageUrlStandardSize = transformAvatarImageUrlSize(avatarImageUrl, '40x40')
  const avatarImageUrlRetina2X = transformAvatarImageUrlSize(avatarImageUrl, '80x80')
  const avatarImageUrlRetina3X = transformAvatarImageUrlSize(avatarImageUrl, '120x120')
  const avatarImageSrcSet = `${avatarImageUrlRetina2X} 2x, ${avatarImageUrlRetina3X} 3x`
  return (
    <LazyImage
      type='img'
      src={avatarImageUrlStandardSize}
      srcSet={avatarImageSrcSet}
      style={{ width: '100%' }}
      alt={userName}
    />
  )
}

function getAvatarImageElement(
  user: User<boolean | undefined>,
  isDefaultAvatar: boolean,
  optimizeAvatarResolution: boolean
) {
  if (isDefaultAvatar)
    return (
      <div className={classNames('default-avatar', userAvatar.getAvatarClass(user.id))}>
        {userAvatar.getAvatarInital(user.name)}
      </div>
    )

  const avatarImageUrl = user.avatar ?? ''

  if (optimizeAvatarResolution && avatarImageUrl) return getOptimizedAvatarImageElement(avatarImageUrl, user.name)

  return <LazyImage type='img' src={avatarImageUrl} style={{ width: '100%' }} alt={user.name} />
}

class UserAvatar<WithRank extends boolean | undefined> extends Component<UserAvatarProps<WithRank>, State> {
  ref: HTMLElement | undefined
  constructor(props: UserAvatarProps<WithRank>) {
    super(props)

    this.state = {
      ref: false,
      disablePopup: (typeof window !== 'undefined' && getInsidedData('device') !== 'desktop') || !!props.disablePopup,
      disableLink: !!props.disableLink
    }
  }

  componentDidMount() {
    this.setState({ ref: true })
  }

  generateUserUrl(user: UserAvatarProps<WithRank>['user']) {
    if (globals('application') === 'control') {
      return `/member/profile?userid=${user.id}`
    }
    return `/members/${user.name.toLowerCase()}-${user.id}`
  }

  getUserUrl(disableLink: boolean, user: User<boolean | undefined>) {
    if (disableLink) return undefined

    if (user.url) return user.url

    return this.generateUserUrl(user)
  }

  render() {
    const { user, extraClass, showRank, style, openInNewTab, optimizeAvatarResolution } = this.props
    const { disablePopup, disableLink } = this.state

    if (user.id) {
      const isDefaultAvatar = userAvatar.isDefaultAvatar(user.avatar)

      const avatarImageElement = getAvatarImageElement(user, isDefaultAvatar, optimizeAvatarResolution ?? false)

      const userUrl = this.getUserUrl(disableLink, user)

      return (
        <div className={classNames('avatar', extraClass)} style={{ float: 'none', ...style }}>
          <div
            className='profilepicture qa-profile-picture'
            ref={(r) => {
              this.ref = r
            }}
          >
            <a
              className='default-avatar-link qa-topic-meta-last-user-icon'
              href={userUrl}
              target={openInNewTab ? '_blank' : undefined}
              rel='noreferrer'
            >
              {avatarImageElement}
              {showRank && user.rank && user.rank.avatarIconUrl ? (
                <div className='profilelabel qa-profile-label'>
                  <img src={user.rank.avatarIconUrl} alt={user.rank.name} title={user.rank.name} />
                </div>
              ) : null}
            </a>
            {this.state.ref && !disablePopup && (
              <ProfilePopup
                hoverControl={this.ref}
                memberId={typeof user.id === 'number' ? user.id : parseInt(user.id)}
              />
            )}
          </div>
        </div>
      )
    }
    return (
      <div className={classNames('avatar', extraClass)} style={{ float: 'none', ...style }}>
        <div
          className={classNames(
            'profilepicture',
            'qa-profile-picture',
            'default-avatar-link',
            'qa-topic-meta-last-user-icon',
            'default-cursor'
          )}
        >
          <div className={classNames('default-avatar', userAvatar.getAvatarClass(user.id))}>
            {userAvatar.getAvatarInital(user.name)}
          </div>
        </div>
      </div>
    )
  }
}

export default UserAvatar
